import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { EmptyChart, ReportCard } from "@/components/reports/ReportCard";
import { fmtDate, monthLabel } from "@/lib/format";
import { Money } from "@/components/Money";

const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);

export const periodLabel = (period, granularity, short = false) =>
  granularity === "monthly" ? monthLabel(period, short) : short ? fmtDate(period).slice(0, 5) : fmtDate(period);

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold text-slate-800">{payload[0].payload.full}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-6"><span style={{ color: p.color }}>{p.name}</span><Money value={p.value} /></div>
      ))}
    </div>
  );
};

const NetDot = ({ cx, cy, payload }) => (cx == null ? null : <circle cx={cx} cy={cy} r={3} fill={payload.net < 0 ? "#DC2626" : "#16A34A"} />);

export const RevenueVsCost = ({ data, params, disabled }) => {
  const g = data.granularity;
  const rows = data.periods.map((p) => ({ label: periodLabel(p.period, g, true), full: periodLabel(p.period, g), revenue: p.revenue, cost: p.total_expenses, net: p.net_profit }));
  const empty = rows.every((r) => !r.revenue && !r.cost);
  const dots = rows.length <= 40;
  return (
    <ReportCard id="revenue_vs_cost" title="Revenue vs Cost" description={`Revenue, total cost (COGS + OpEx + Other) and net profit per ${g === "monthly" ? "month" : "day"} · ${rows.length} points`} params={params} disabled={disabled}>
      {empty ? <EmptyChart testid="revenue-chart-empty" /> : (
        <div className="h-80" data-testid="revenue-chart" data-points={rows.length}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} minTickGap={12} />
              <YAxis tickFormatter={kfmt} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} width={48} />
              <Tooltip content={<Tip />} />
              <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#94A3B8" />
              <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#0D9488" strokeWidth={2.25} dot={dots ? { r: 2.5 } : false} isAnimationActive={false} />
              <Line type="monotone" dataKey="cost" name="Total expenses" stroke="#64748B" strokeWidth={2} strokeDasharray="5 4" dot={dots ? { r: 2.5 } : false} isAnimationActive={false} />
              <Line type="monotone" dataKey="net" name="Net profit" stroke="#16A34A" strokeWidth={2.5} dot={dots ? <NetDot /> : false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </ReportCard>
  );
};
